import React, { useMemo } from 'react'
import {
  DataGrid,
  GridRenderCellParams,
  GridValueGetterParams,
  GridValueFormatterParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  gridClasses,
  GridValidRowModel,
  GridColDef,
} from '@mui/x-data-grid'
import { Button, Grid } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { Alert, Service } from '../../../schema'
import { DateTime, Duration } from 'luxon'
import AppLink from '../../util/AppLink'
import { useWorker } from '../../worker'
import { pathPrefix } from '../../env'

interface AlertMetricsTableProps {
  alerts: Alert[]
  loading: boolean
  serviceName: string
  startTime: string
  endTime: string
}



const columns: GridColDef[] = [
  {
    field: 'alertID',
    headerName: 'Alert ID',
    width: 100,
    renderCell: (params: GridRenderCellParams<GridValidRowModel>) => (
      <AppLink to={`/alerts/${params.row.alertID}`}>{params.value}</AppLink>
    ),
  },
  {
    field: 'serviceName',
    headerName: 'Service Name',
    width: 200,
    valueGetter: (params: GridValueGetterParams) => {
      return params.row.service?.name || ''
    },
    renderCell: (params: GridRenderCellParams<{ service: Service }>) => {
      if (params.row.service?.id && params.value) {
        return (
          <AppLink to={`/services/${params.row.service.id}`}>
            {params.value}
          </AppLink>
        )
      }
      return ''
    },
  },
  {
    field: 'summary',
    headerName: 'Summary',
    width: 250,
    renderCell: (params) => (
      <div style={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
        {params.value}
      </div>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 250,
    renderCell: (params: GridRenderCellParams<GridValidRowModel>) => {
      const status = params.row.status
      const event = params.row.recentEvents?.nodes?.[0]
      const subject = event?.subject
      
      if (status === 'StatusUnacknowledged') {
        if (event?.message?.includes('Escalated')) {
            if (subject?.name) {
                return (
                    <span>
                        Escalated by{' '}
                         <AppLink to={`/users/${subject.id}`}>
                            {subject.name}
                        </AppLink>
                    </span>
                )
            }
            return 'Auto Escalated'
        }
        return 'Unacknowledged'
      }

      let action = ''
      if (status === 'StatusAcknowledged') action = 'Acknowledged by'
      if (status === 'StatusClosed') action = 'Closed by'

      if (!subject) return status.replace('Status', '')

      if (subject.type === 'User') {
          return (
              <span>
                  {action}{' '}
                  <AppLink to={`/users/${subject.id}`}>
                      {subject.name}
                  </AppLink>
              </span>
          )
      }
      
      return `${action} ${subject.name}`
    },
  },
  {
    field: 'escalationCount',
    headerName: 'Escalations',
    width: 100,
    valueGetter: (params: GridValueGetterParams) => params.row.escalationCount || 0,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    width: 220,
    valueFormatter: (params: GridValueFormatterParams) =>
      DateTime.fromISO(params.value as string).toFormat('ccc, DD, t ZZZZ'),
  },
  {
    field: 'closedAt',
    headerName: 'Closed At',
    width: 220,
    valueFormatter: (params: GridValueFormatterParams) =>
      params.value
        ? DateTime.fromISO(params.value as string).toFormat('ccc, DD, t ZZZZ')
        : '',
  },
  {
    field: 'timeToAck',
    headerName: 'MTTA',
    width: 100,
    valueFormatter: (params: GridValueFormatterParams) =>
      params.value
        ? Duration.fromISO(params.value as string).toFormat('hh:mm:ss')
        : '',
  },
  {
    field: 'timeToClose',
    headerName: 'MTTR',
    width: 100,
    valueFormatter: (params: GridValueFormatterParams) =>
      params.value
        ? Duration.fromISO(params.value as string).toFormat('hh:mm:ss')
        : '',
  },
  {
    field: 'timeToResolved',
    headerName: 'Resolved Time',
    width: 120,
    valueGetter: (params: GridValueGetterParams) => {
      if (!params.row.timeToClose || !params.row.timeToAck) return ''
      const timeToClose = Duration.fromISO(params.row.timeToClose)
      const timeToAck = Duration.fromISO(params.row.timeToAck)
      return timeToClose.minus(timeToAck).toFormat('hh:mm:ss')
    },
  },
  { field: 'noiseReason', headerName: 'Noise Reason', width: 120 },
  {
    field: 'details',
    headerName: 'Details',
    width: 250,
    renderCell: (params) => (
      <div style={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
        {params.value}
      </div>
    ),
  },
]

export default function AlertMetricsTable(
  props: AlertMetricsTableProps,
): JSX.Element {
  const alerts = useMemo(
    () => props.alerts.map((a) => ({ ...a, ...a.metrics })),
    [props.alerts],
  )

  const csvOpts = useMemo(
    () => ({
      urlPrefix: location.origin + pathPrefix,
      alerts,
    }),
    [props.alerts],
  )
  const [csvData] = useWorker('useAlertCSV', csvOpts, '')
  const link = useMemo(
    () => URL.createObjectURL(new Blob([csvData], { type: 'text/csv' })),
    [csvData],
  )

  function CustomToolbar(): JSX.Element {
    return (
      <GridToolbarContainer className={gridClasses.toolbarContainer}>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
          </Grid>
          <Grid item>
            <AppLink
              to={link}
              download={`${props.serviceName.replace(
                /[^a-z0-9]/gi,
                '_',
              )}-metrics-${props.startTime}-to-${props.endTime}.csv`}
            >
              <Button
                size='small'
                startIcon={<DownloadIcon fontSize='small' />}
              >
                Export
              </Button>
            </AppLink>
          </Grid>
        </Grid>
      </GridToolbarContainer>
    )
  }

  return (
    <Grid
      container
      item
      xs={12}
      sx={{ height: '500px' }}
      data-cy='metrics-table'
    >
      <DataGrid
        rows={alerts}
        loading={props.loading}
        columns={columns}
        rowSelection={false}
        slots={{
          toolbar: CustomToolbar,
        }}
        getRowHeight={() => 'auto'}
      />
    </Grid>
  )
}
